import { useState, useCallback, useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet-toolbar';
import 'leaflet-toolbar/dist/leaflet.toolbar.css';
import 'leaflet-distortableimage';
import 'leaflet-distortableimage/dist/leaflet.distortableimage.css';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

// Disable all keyboard shortcuts from leaflet-distortableimage — they conflict
// with sidebar text inputs and cause accidental mode changes (e.g. 'd' for drag).
if (L.DistortableImage) L.DistortableImage.action_map = {};
if (L.distortableImage) L.distortableImage.group_action_map = {};

const FEET_PER_METER = 3.280839895;
const DEFAULT_BLEND_MODE = 'normal';
const WHITE_BACKGROUND_BLEND_MODE = 'multiply';
const MAX_CROP_PERCENT = 90;

const normalizeCrop = (crop) => ({
  top: Math.min(MAX_CROP_PERCENT, Math.max(0, Number(crop?.top || 0))),
  right: Math.min(MAX_CROP_PERCENT, Math.max(0, Number(crop?.right || 0))),
  bottom: Math.min(MAX_CROP_PERCENT, Math.max(0, Number(crop?.bottom || 0))),
  left: Math.min(MAX_CROP_PERCENT, Math.max(0, Number(crop?.left || 0))),
});

const hasCrop = (crop) => {
  const c = normalizeCrop(crop);
  return c.top > 0 || c.right > 0 || c.bottom > 0 || c.left > 0;
};

/**
 * Manages plan overlay images (PNG/JPG) as distortable Leaflet layers.
 *
 * Overlays are stored on the takeoff document (takeoff.overlays). This hook:
 * - Loads overlays from the takeoff and renders them on the map
 * - Lets the user upload new images (content-hash deduplicated server-side)
 * - Manages opacity, visibility, locking, category/subType
 * - Tracks an "active" overlay — only the active one is draggable, to
 *   prevent accidental interaction with locked overlays underneath
 * - Returns an array of overlay objects that can be saved back to the takeoff
 *
 * Overlay shape:
 *   {
 *     id: string (client-generated uuid, used as a key),
 *     imageUrl: string,
 *     corners: [{ lat, lng }, { lat, lng }, { lat, lng }, { lat, lng }],
 *     opacity: number (0-1),
 *     visible: boolean,
 *     locked: boolean,
 *     category: 'aerial' | 'drawing' | 'plans' | '',
 *     subType: string,
 *   }
 */
export default function useOverlays({ url, mapRef, jobsite, initialOverlays }) {
  const [overlays, setOverlays] = useState([]);
  const [activeOverlayId, setActiveOverlayId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [savedOverlayUrls, setSavedOverlayUrls] = useState([]); // previously uploaded for this jobsite
  const [scaleCalibration, setScaleCalibration] = useState(null);
  const [scalePreviewPoint, setScalePreviewPoint] = useState(null);
  const layersRef = useRef({}); // overlayId -> distortableImageOverlay
  const calibrationLayerRef = useRef(null);
  const initializedRef = useRef(false);

  const markDirty = useCallback(() => {
    setDirty(true);
  }, []);

  const applyOverlayAppearance = useCallback((layer, overlay = {}) => {
    const image = layer?.getElement?.();
    if (!image) return;

    image.style.mixBlendMode = overlay.blendMode === WHITE_BACKGROUND_BLEND_MODE ? WHITE_BACKGROUND_BLEND_MODE : '';

    const crop = normalizeCrop(overlay.crop);
    const clipPath = hasCrop(crop)
      ? `inset(${crop.top}% ${crop.right}% ${crop.bottom}% ${crop.left}%)`
      : '';
    image.style.clipPath = clipPath;
    image.style.WebkitClipPath = clipPath;
  }, []);

  const bindLayerDirtyTracking = useCallback((layer) => {
    if (!layer?.on) return;
    layer.on('update', markDirty);
    layer.on('restore', markDirty);
  }, [markDirty]);

  const selectEditableLayer = useCallback((layer) => {
    if (!layer?.editing) return;
    try {
      if (!layer.editing.enabled()) layer.editing.enable();
      if (layer.editing.isMode?.('lock')) layer.editing._unlock();
    } catch (_) {}

    const select = () => {
      try { layer.select?.(); } catch (_) {}
    };
    const image = layer.getElement?.();
    if (image?.complete) {
      select();
    } else if (layer.once) {
      layer.once('load', select);
    } else {
      setTimeout(select, 0);
    }
  }, []);

  const disableEditableLayer = useCallback((layer) => {
    if (!layer?.editing) return;
    try {
      layer.deselect?.();
      if (layer.editing.enabled()) layer.editing.disable();
    } catch (_) {}
  }, []);

  // Restore overlays from the loaded takeoff
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    // Clear existing layers
    Object.values(layersRef.current).forEach(layer => {
      try { layer.remove(); } catch (_) {}
    });
    layersRef.current = {};

    if (!Array.isArray(initialOverlays) || initialOverlays.length === 0) {
      setOverlays([]);
      setActiveOverlayId(null);
      setDirty(false);
      return;
    }

    // Defer a tick to make sure the map's marker pane exists
    const doRestore = () => {
      const restored = [];
      initialOverlays.forEach((ov) => {
        if (!ov?.imageUrl || !ov?.corners?.length || ov.corners.length !== 4) return;
        const id = uuidv4();
        const corners = ov.corners.map(c => L.latLng(c.lat, c.lng));
        const opacity = ov.opacity ?? 0.6;
        const isLocked = !!ov.locked;

        const crop = normalizeCrop(ov.crop);
        try {
          const layer = L.distortableImageOverlay(ov.imageUrl, {
            corners,
            mode: isLocked ? 'lock' : 'rotate',
            selected: false,
            opacity,
          }).addTo(map);

          bindLayerDirtyTracking(layer);
          layersRef.current[id] = layer;
          restored.push({
            id,
            imageUrl: ov.imageUrl,
            visible: true,
            opacity,
            locked: isLocked,
            category: ov.category || '',
            subType: ov.subType || '',
            blendMode: ov.blendMode || DEFAULT_BLEND_MODE,
            crop,
          });
          applyOverlayAppearance(layer, { ...ov, crop });
        } catch (err) {
          console.warn('Failed to restore overlay:', err);
        }
      });
      setOverlays(restored);
      setDirty(false);
      initializedRef.current = true;
    };

    if (map.getPane('markerPane')) {
      doRestore();
    } else {
      map.whenReady(doRestore);
    }

    return () => {
      Object.values(layersRef.current).forEach(layer => {
        try { layer.remove(); } catch (_) {}
      });
      layersRef.current = {};
    };
  }, [applyOverlayAppearance, bindLayerDirtyTracking, initialOverlays, mapRef]);

  // Sync pointer events and editing state based on activeOverlayId + locked
  useEffect(() => {
    overlays.forEach(ov => {
      const layer = layersRef.current[ov.id];
      if (!layer) return;

      // Interactive only if: visible, active, unlocked, and not collecting scale points.
      const shouldBeInteractive =
        ov.visible &&
        ov.id === activeOverlayId &&
        !ov.locked &&
        !scaleCalibration;
      const el = layer.getElement();
      const container = layer._image?.parentElement;
      applyOverlayAppearance(layer, ov);

      if (shouldBeInteractive) {
        selectEditableLayer(layer);
        if (el) {
          el.style.pointerEvents = '';
          el.style.zIndex = '650';
        }
        if (container) container.style.pointerEvents = '';
      } else {
        disableEditableLayer(layer);
        if (el) {
          el.style.pointerEvents = 'none';
          el.style.zIndex = '';
        }
        if (container) container.style.pointerEvents = 'none';
      }
    });
  }, [activeOverlayId, applyOverlayAppearance, disableEditableLayer, overlays, scaleCalibration, selectEditableLayer]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return undefined;

    const container = map.getContainer?.();
    const previousCursor = container?.style?.cursor || '';
    if (scaleCalibration && container) container.style.cursor = 'crosshair';

    return () => {
      if (container) container.style.cursor = previousCursor;
    };
  }, [mapRef, scaleCalibration]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return undefined;

    if (calibrationLayerRef.current) {
      try { map.removeLayer(calibrationLayerRef.current); } catch (_) {}
      calibrationLayerRef.current = null;
    }

    if (!scaleCalibration) {
      setScalePreviewPoint(null);
      return undefined;
    }

    const group = L.layerGroup().addTo(map);
    calibrationLayerRef.current = group;
    const points = scaleCalibration.points || [];

    points.forEach((point, index) => {
      L.circleMarker(point, {
        radius: 6,
        color: '#ffffff',
        weight: 2,
        fillColor: '#1976d2',
        fillOpacity: 1,
        interactive: false,
      }).bindTooltip(`Point ${index + 1}`, {
        permanent: true,
        direction: 'top',
        offset: [0, -8],
        className: 'site-takeoff-calibration-tooltip',
      }).addTo(group);
    });

    if (points.length === 1 && scalePreviewPoint) {
      L.polyline([points[0], scalePreviewPoint], {
        color: '#1976d2',
        weight: 2,
        dashArray: '4,8',
        interactive: false,
      }).addTo(group);
    } else if (points.length === 2) {
      L.polyline(points, {
        color: '#1976d2',
        weight: 3,
        dashArray: '6,6',
        interactive: false,
      }).addTo(group);
    }

    return () => {
      if (calibrationLayerRef.current === group) {
        try { map.removeLayer(group); } catch (_) {}
        calibrationLayerRef.current = null;
      }
    };
  }, [mapRef, scaleCalibration, scalePreviewPoint]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !scaleCalibration || (scaleCalibration.points || []).length !== 1) {
      setScalePreviewPoint(null);
      return undefined;
    }
    const onMouseMove = (event) => setScalePreviewPoint(event.latlng);
    map.on('mousemove', onMouseMove);
    return () => {
      map.off('mousemove', onMouseMove);
    };
  }, [mapRef, scaleCalibration]);

  useEffect(() => {
    if (!scaleCalibration) return undefined;
    const map = mapRef.current;
    if (!map) return undefined;

    const onMapClick = (event) => {
      setScaleCalibration(prev => {
        if (!prev || prev.overlayId !== scaleCalibration.overlayId) return prev;
        const nextPoints = [...(prev.points || []), event.latlng].slice(0, 2);
        if (nextPoints.length < 2) {
          return { ...prev, points: nextPoints };
        }

        const layer = layersRef.current[prev.overlayId];
        const currentMeters = nextPoints[0].distanceTo(nextPoints[1]);
        const targetMeters = prev.distanceFeet / FEET_PER_METER;
        if (!layer || !Number.isFinite(currentMeters) || currentMeters <= 0 || !Number.isFinite(targetMeters) || targetMeters <= 0) {
          return null;
        }

        try {
          const ratio = targetMeters / currentMeters;
          const zoom = map.getZoom();
          const centerPoint = map.project(layer.getCenter(), zoom);
          const scaledCorners = layer.getCorners().map(corner => {
            const point = map.project(corner, zoom);
            return map.unproject(
              L.point(
                centerPoint.x + ((point.x - centerPoint.x) * ratio),
                centerPoint.y + ((point.y - centerPoint.y) * ratio)
              ),
              zoom
            );
          });
          layer.setCorners(scaledCorners);
          setActiveOverlayId(prev.overlayId);
          setOverlays(items => items.map(item => (
            item.id === prev.overlayId ? { ...item, locked: false, visible: true } : item
          )));
          markDirty();
          setTimeout(() => selectEditableLayer(layer), 0);
        } catch (err) {
          console.warn('Failed to calibrate overlay scale:', err);
        }
        setScalePreviewPoint(null);
        return null;
      });
    };

    map.on('click', onMapClick);
    return () => {
      map.off('click', onMapClick);
    };
  }, [mapRef, markDirty, scaleCalibration, selectEditableLayer]);

  // Fetch previously-saved overlay URLs for this jobsite (for the "re-use" picker)
  useEffect(() => {
    if (!url || !jobsite?._id) {
      setSavedOverlayUrls([]);
      return;
    }
    axios
      .get(`${url}/api/customer/site-takeoffs?jobSiteId=${jobsite._id}`)
      .then(resp => {
        setSavedOverlayUrls(resp.data?.overlayUrls || []);
      })
      .catch(() => setSavedOverlayUrls([]));
  }, [url, jobsite]);

  // ===== Upload a new overlay image =====
  const uploadOverlay = useCallback(async (file) => {
    if (!url || !jobsite?._id || !file) return null;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('jobSiteId', jobsite._id);
      const resp = await axios.post(
        `${url}/api/customer/site-takeoff-overlay-upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      if (resp.data?.success && resp.data?.objectUrl) {
        const objectUrl = resp.data.objectUrl;
        setSavedOverlayUrls(prev => prev.includes(objectUrl) ? prev : [...prev, objectUrl]);
        addOverlayToMap(objectUrl);
        return objectUrl;
      }
    } catch (err) {
      console.error('uploadOverlay error:', err);
    } finally {
      setUploading(false);
    }
    return null;
  }, [url, jobsite]);

  // Add an overlay to the map centered on the current view
  const addOverlayToMap = useCallback((imgUrl) => {
    const map = mapRef.current;
    if (!map || !imgUrl) return;

    const id = uuidv4();
    const center = map.getCenter();
    const bounds = map.getBounds();
    const latSpan = (bounds.getNorth() - bounds.getSouth()) * 0.25;
    const lngSpan = (bounds.getEast() - bounds.getWest()) * 0.25;

    const corners = [
      L.latLng(center.lat + latSpan, center.lng - lngSpan),
      L.latLng(center.lat + latSpan, center.lng + lngSpan),
      L.latLng(center.lat - latSpan, center.lng - lngSpan),
      L.latLng(center.lat - latSpan, center.lng + lngSpan),
    ];

    try {
      const layer = L.distortableImageOverlay(imgUrl, {
        corners,
        mode: 'rotate',
        selected: false,
        opacity: 0.6,
      }).addTo(map);

      bindLayerDirtyTracking(layer);
      layersRef.current[id] = layer;
      setOverlays(prev => [
        ...prev,
        {
          id,
          imageUrl: imgUrl,
          visible: true,
          opacity: 0.6,
          locked: false,
          category: '',
          subType: '',
          blendMode: DEFAULT_BLEND_MODE,
          crop: normalizeCrop(),
        },
      ]);
      markDirty();
      // Auto-activate the new overlay so the user can start aligning it
      setActiveOverlayId(id);
    } catch (err) {
      console.error('addOverlayToMap error:', err);
    }
  }, [bindLayerDirtyTracking, mapRef, markDirty]);

  // ===== Mutations =====
  const setOverlayVisible = useCallback((ovId, visible) => {
    const layer = layersRef.current[ovId];
    if (layer) {
      if (visible) {
        setOverlays(prev => {
          const ov = prev.find(o => o.id === ovId);
          if (ov) layer.setOpacity(ov.opacity);
          return prev;
        });
      } else {
        try { layer.deselect(); } catch (_) {}
        layer.setOpacity(0);
      }
    }
    setOverlays(prev => prev.map(o => o.id === ovId ? { ...o, visible } : o));
    markDirty();
    if (!visible && activeOverlayId === ovId) setActiveOverlayId(null);
  }, [activeOverlayId, markDirty]);

  const setOverlayOpacity = useCallback((ovId, opacity) => {
    const layer = layersRef.current[ovId];
    if (layer) layer.setOpacity(opacity);
    setOverlays(prev => prev.map(o => o.id === ovId ? { ...o, opacity } : o));
    markDirty();
  }, [markDirty]);

  const setMasterOpacity = useCallback((opacity) => {
    setOverlays(prev => prev.map(o => {
      const layer = layersRef.current[o.id];
      if (layer && o.visible) layer.setOpacity(opacity);
      return { ...o, opacity };
    }));
    markDirty();
  }, [markDirty]);

  const setOverlayLocked = useCallback((ovId, locked) => {
    setOverlays(prev => prev.map(o => o.id === ovId ? { ...o, locked } : o));
    markDirty();
    if (locked) {
      setActiveOverlayId(prev => prev === ovId ? null : prev);
    } else {
      setActiveOverlayId(ovId);
    }
    setScaleCalibration(prev => prev?.overlayId === ovId ? null : prev);
  }, [markDirty]);

  const setOverlayCategory = useCallback((ovId, field, value) => {
    setOverlays(prev => prev.map(o => {
      if (o.id !== ovId) return o;
      if (field === 'category') return { ...o, category: value, subType: '' };
      return { ...o, [field]: value };
    }));
    markDirty();
  }, [markDirty]);

  const setOverlayBlendMode = useCallback((ovId, blendMode) => {
    const resolved = blendMode === WHITE_BACKGROUND_BLEND_MODE ? WHITE_BACKGROUND_BLEND_MODE : DEFAULT_BLEND_MODE;
    const layer = layersRef.current[ovId];
    const current = overlays.find(o => o.id === ovId);
    applyOverlayAppearance(layer, { ...current, blendMode: resolved });
    setOverlays(prev => prev.map(o => o.id === ovId ? { ...o, blendMode: resolved } : o));
    markDirty();
  }, [applyOverlayAppearance, markDirty, overlays]);

  const setOverlayCrop = useCallback((ovId, cropUpdates) => {
    setOverlays(prev => prev.map(o => {
      if (o.id !== ovId) return o;
      const crop = normalizeCrop({ ...(o.crop || {}), ...(cropUpdates || {}) });
      applyOverlayAppearance(layersRef.current[ovId], { ...o, crop });
      return { ...o, crop };
    }));
    markDirty();
  }, [applyOverlayAppearance, markDirty]);

  const resetOverlayCrop = useCallback((ovId) => {
    const crop = normalizeCrop();
    setOverlays(prev => prev.map(o => {
      if (o.id !== ovId) return o;
      applyOverlayAppearance(layersRef.current[ovId], { ...o, crop });
      return { ...o, crop };
    }));
    markDirty();
  }, [applyOverlayAppearance, markDirty]);

  const activateOverlay = useCallback((ovId) => {
    setScaleCalibration(null);
    const currentOverlay = overlays.find(item => item.id === ovId);
    setActiveOverlayId(prev => {
      const next = prev === ovId ? null : ovId;
      if (next) {
        setOverlays(items => items.map(item => (
          item.id === ovId ? { ...item, locked: false, visible: true } : item
        )));
        if (currentOverlay?.locked || currentOverlay?.visible === false) markDirty();
      }
      return next;
    });
  }, [markDirty, overlays]);

  const removeOverlay = useCallback((ovId) => {
    const layer = layersRef.current[ovId];
    if (layer) {
      try { layer.deselect(); layer.remove(); } catch (_) {}
      delete layersRef.current[ovId];
    }
    setOverlays(prev => prev.filter(o => o.id !== ovId));
    markDirty();
    if (activeOverlayId === ovId) setActiveOverlayId(null);
    setScaleCalibration(prev => prev?.overlayId === ovId ? null : prev);
  }, [activeOverlayId, markDirty]);

  // Add an existing (previously uploaded) overlay URL to the map
  const addExistingOverlay = useCallback((imgUrl) => {
    addOverlayToMap(imgUrl);
  }, [addOverlayToMap]);

  const startScaleCalibration = useCallback((ovId, distanceFeet) => {
    const parsed = Number(distanceFeet);
    if (!ovId || !Number.isFinite(parsed) || parsed <= 0) return false;
    const layer = layersRef.current[ovId];
    if (!layer) return false;
    setOverlays(prev => prev.map(o => (
      o.id === ovId ? { ...o, locked: false, visible: true } : o
    )));
    setActiveOverlayId(ovId);
    setScaleCalibration({ overlayId: ovId, distanceFeet: parsed, points: [] });
    return true;
  }, []);

  const cancelScaleCalibration = useCallback(() => {
    setScaleCalibration(null);
  }, []);

  const clearDirty = useCallback(() => {
    setDirty(false);
  }, []);

  // ===== Serialize current overlays for saving back to the takeoff =====
  // Reads corners from the live layers so any drag changes are captured.
  const serializeForSave = useCallback(() => {
    return overlays
      .map(ov => {
        const layer = layersRef.current[ov.id];
        if (!layer) return null;
        try {
          const corners = layer.getCorners();
          const imgUrl = layer._url || layer.getElement()?.src;
          if (corners?.length === 4 && imgUrl) {
            return {
              imageUrl: imgUrl,
              corners: corners.map(c => ({ lat: c.lat, lng: c.lng })),
              opacity: ov.opacity ?? 0.6,
              locked: !!ov.locked,
              category: ov.category || '',
              subType: ov.subType || '',
              blendMode: ov.blendMode || DEFAULT_BLEND_MODE,
              crop: normalizeCrop(ov.crop),
            };
          }
        } catch (e) {
          console.warn('Failed to serialize overlay:', e);
        }
        return null;
      })
      .filter(Boolean);
  }, [overlays]);

  return {
    overlays,
    activeOverlayId,
    uploading,
    dirty,
    savedOverlayUrls,
    uploadOverlay,
    addExistingOverlay,
    setOverlayVisible,
    setOverlayOpacity,
    setMasterOpacity,
    setOverlayLocked,
    setOverlayCategory,
    setOverlayBlendMode,
    setOverlayCrop,
    resetOverlayCrop,
    activateOverlay,
    scaleCalibration,
    startScaleCalibration,
    cancelScaleCalibration,
    removeOverlay,
    serializeForSave,
    clearDirty,
  };
}
